export interface OnboardingProfile {
  companyName: string;
  companyAddress: string;
  supportEmail: string;
  invoicePrefix: string;
  timezone: string;
}

export interface OnboardingBranding {
  logo_url: string;
  app_name: string;
  primary_color: string;
  secondary_color: string;
  favicon_url: string;
}

export type OnboardingTaskId = 'company_info' | 'branding' | 'stripe' | 'team' | 'first_task';
export interface OnboardingStatus {
  onboarding_completed: boolean;
  onboarding_dismissed: boolean;
  steps: Record<string, boolean>;
  all_steps_complete: boolean;
  revision: string;
  workspace_ready: boolean;
  payment_ready: boolean;
  payment_health: { status: 'healthy' | 'warning' | 'blocked' | 'stale' | 'unavailable' | 'not_connected'; reasons: string[]; checked_at: string | null; connected_account_suffix?: string | null; stripe_mode?: string | null };
  environment: string;
  company: { id: string; name: string; slug: string };
  profile: OnboardingProfile;
  branding: OnboardingBranding;
  tasks: Array<{
    id: OnboardingTaskId;
    title: string;
    description: string;
    required: boolean;
    status: 'complete' | 'incomplete' | 'blocked' | 'optional';
    href: string;
    owner: 'operator' | 'operations';
    last_updated_at: string | null;
  }>;
  saved_at: string | null;
}

export const onboardingKey = (companyId?: string) => ['onboarding-status', companyId || 'unscoped'];
export const ONBOARDING_BEFORE_LEAVE = 'modal:onboarding-before-leave';
export const requestOnboardingLeave = () => window.dispatchEvent(new Event(ONBOARDING_BEFORE_LEAVE, { cancelable: true }));

const COMMON_TIMEZONES = ['America/New_York', 'America/Chicago', 'America/Denver', 'America/Phoenix', 'America/Los_Angeles', 'America/Anchorage', 'Pacific/Honolulu', 'UTC'];
export function onboardingTimezones(current: string): string[] {
  const supported = (Intl as typeof Intl & { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf?.('timeZone') || [];
  let validCurrent = false;
  try { new Intl.DateTimeFormat('en', { timeZone: current }); validCurrent = Boolean(current); } catch { /* Invalid stored values must be corrected. */ }
  return Array.from(new Set([...COMMON_TIMEZONES, ...(validCurrent ? [current] : []), ...supported]));
}

export function validateProfile(profile: OnboardingProfile): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!profile.companyName.trim()) errors.companyName = 'Enter the business name.';
  else if (profile.companyName.trim().length > 200) errors.companyName = 'Use up to 200 characters for the business name.';
  if (!profile.companyAddress.trim() || profile.companyAddress.trim().length > 255) errors.companyAddress = 'Enter a business address of up to 255 characters.';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profile.supportEmail) || profile.supportEmail.length > 256) errors.supportEmail = 'Enter a valid support email of up to 256 characters.';
  if (!/^[A-Za-z0-9][A-Za-z0-9_-]{0,19}$/.test(profile.invoicePrefix)) errors.invoicePrefix = 'Start with a letter or number; use up to 20 letters, numbers, hyphens or underscores.';
  try { new Intl.DateTimeFormat('en', { timeZone: profile.timezone }).format(); }
  catch { errors.timezone = 'Enter a valid timezone, such as America/New_York.'; }
  if (!profile.timezone.trim() || profile.timezone.length > 50) errors.timezone = 'Enter a valid timezone of up to 50 characters.';
  return errors;
}

export function safeImageUrl(value: string): boolean {
  if (!value) return true;
  if (/[\\\r\n]/.test(value)) return false;
  if (value.startsWith('/') && !value.startsWith('//')) return true;
  try { const url = new URL(value); return url.protocol === 'https:' && !url.username && !url.password; }
  catch { return false; }
}

export function validateBranding(branding: OnboardingBranding): Record<string, string> {
  const errors: Record<string, string> = {};
  if (branding.app_name.trim().length > 200) errors.app_name = 'Use up to 200 characters for the display name.';
  for (const field of ['primary_color', 'secondary_color'] as const) {
    if (branding[field] && !/^#[0-9a-f]{6}$/i.test(branding[field])) errors[field] = 'Choose a six-digit hex color.';
  }
  for (const field of ['logo_url', 'favicon_url'] as const) {
    if (branding[field].trim().length > 1000 || !safeImageUrl(branding[field].trim())) errors[field] = 'Use an HTTPS image URL or a same-origin / path of up to 1000 characters.';
  }
  return errors;
}

export function contrastOnWhite(hex: string): number {
  if (!/^#[0-9a-f]{6}$/i.test(hex)) return 1;
  const rgb = [1, 3, 5].map((offset) => parseInt(hex.slice(offset, offset + 2), 16) / 255)
    .map((value) => value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4);
  return 1.05 / (rgb[0] * 0.2126 + rgb[1] * 0.7152 + rgb[2] * 0.0722 + 0.05);
}

// Operations exposes a smaller set of routes than the tenant administration shell.
export function onboardingTaskHref(href: string, operations: boolean): string | null {
  if (!operations) return /^\/admin(?:\/|$)/.test(href) && !href.includes('\\') ? href : null;
  const path = href.split('?')[0];
  if (path === '/admin/assistant' || path === '/operations/assistant') return '/operations/assistant';
  if (path === '/admin/vehicles') return '/operations/admin/vehicles';
  if (path === '/admin/onboarding' || path === '/operations/setup') return '/operations/setup';
  return null;
}
